import React, { useEffect, useState } from 'react';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Dialog } from '@mui/material';
import { functions } from '../../firebase';
import { Card } from '../today/cards';
import { PaperBlock } from '../../components/buyerForm/BuyerFormApp';
import { DOCS, OCCUPATIONS, fullName, mobileGroups, panNameDiffers, type Buyer, type DocsByBuyer } from '../../components/buyerForm/buyerFormModel';
import '../../components/buyerForm/buyerForm.css';

type FormDoc = {
  status?: 'draft' | 'submitted' | 'approved';
  buyers?: Buyer[];
  docs?: DocsByBuyer;
  submittedVia?: 'link' | 'office';
  submittedAt?: any; approvedAt?: any; approvedBy?: string;
  societyCommittee?: string; termsVersion?: string; termsAcceptedAt?: any; checkedWithBuyer?: boolean;
  otp?: Record<string, { at?: any; hash?: string }>;
  link?: { phoneHint?: string; sentToName?: string | null; sentAt?: any; expiresAt?: any; openedAt?: any; revoked?: boolean };
};

const callInvite = httpsCallable<any, any>(functions, 'buyerFormInvite');
const callReview = httpsCallable<any, any>(functions, 'buyerFormReview');
const ms = (v: any) => (typeof v?.toMillis === 'function' ? v.toMillis() : 0);
const day = (v: any) => (ms(v) ? new Date(ms(v)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '');
const errText = (e: any) => String(e?.message || 'Something went wrong.').replace(/^Firebase(Error)?:\s*/i, '');
const RELATION: Record<string, string> = { son: 'Son of', daughter: 'Daughter of', wife: 'Wife of' };

/** Unit page: send the buyer details link, open the form at the office, and approve what buyers send. */
export function useBuyerFormDoc(projectId: string, unitId: string, bookingId: string) {
  const [form, setForm] = useState<FormDoc | null>(null);
  const [termsReady, setTermsReady] = useState<boolean | null>(null);
  const [termMonths, setTermMonths] = useState<number | null>(null);
  const [signing, setSigning] = useState<Record<string, Record<string, boolean>>>({});
  const [sentToAdvocate, setSentToAdvocate] = useState(false);
  useEffect(() => {
    setSentToAdvocate(false);
    if (!bookingId || !projectId || !unitId) return undefined;
    return onSnapshot(
      doc(getFirestore(), 'projects', projectId, 'units', unitId, 'bookings', bookingId, 'buyerForm', 'advocatePack'),
      (s) => setSentToAdvocate(!!s.get('sentAt')),
      () => setSentToAdvocate(false)
    );
  }, [bookingId, projectId, unitId]);
  useEffect(() => {
    if (!bookingId || !projectId || !unitId) { setForm(null); return undefined; }
    return onSnapshot(
      doc(getFirestore(), 'projects', projectId, 'units', unitId, 'bookings', bookingId, 'buyerForm', 'current'),
      (s) => setForm(s.exists() ? (s.data() as FormDoc) : null),
      () => setForm(null)
    );
  }, [bookingId, projectId, unitId]);
  useEffect(() => {
    if (!bookingId || !projectId || !unitId) { setTermsReady(false); setTermMonths(null); setSigning({}); return undefined; }
    setTermsReady(null);
    return onSnapshot(
      doc(getFirestore(), 'projects', projectId, 'units', unitId, 'bookings', bookingId),
      (s) => {
        const b = s.data() as any;
        setTermsReady(!!b?.paymentCondition || !!String(b?.paymentTerms || '').trim());
        setTermMonths(Number(b?.agreementTermMonths) || null);
        setSigning(b?.signing || {});
      },
      () => setTermsReady(false)
    );
  }, [bookingId, projectId, unitId]);
  return { form, termsReady, termMonths, signing, sentToAdvocate };
}

/** Unit page: send the buyer details link and open the form at the office. Review opens from the page. */
export function BuyerFormCard({ projectId, unitId, bookingId, form, termsReady, signed, defaultMobile, onFillHere, onAddTerms, onReview }: {
  projectId: string; unitId: string; bookingId: string;
  form: FormDoc | null;
  termsReady: boolean | null;
  /** The signed booking form is uploaded: reopening needs a fresh signature. */
  signed: boolean;
  defaultMobile: string;
  onFillHere: () => void;
  /** The form goes out only after payment terms are saved on the booking. */
  onAddTerms: () => void;
  onReview: () => void;
}) {
  const [sendOpen, setSendOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const place = { projectId, unitId, bookingId };
  const review = async (decision: 'reopen' | 'revoke') => {
    if (decision === 'reopen' && signed && !window.confirm('The signed booking form is already uploaded. If details change, a corrected form must be signed again. Reopen anyway?')) return;
    setBusy(decision);
    setMsg('');
    try {
      await callReview({ ...place, decision });
      setMsg(decision === 'reopen' ? 'Reopened. The buyer can change the details again.' : 'Link stopped.');
    } catch (e) {
      setMsg(errText(e));
    } finally {
      setBusy('');
    }
  };

  const status = form?.status || (form?.link ? 'draft' : '');
  const linkLive = !!form?.link && !form.link.revoked && ms(form.link.expiresAt) > Date.now();
  const line = (() => {
    if (status === 'approved') return `Approved ${day(form?.approvedAt)}${form?.submittedVia === 'office' ? ', filled at the office' : ''}`;
    if (status === 'submitted') return `Sent by ${form?.submittedVia === 'office' ? 'the office' : 'the buyer'} ${day(form?.submittedAt)}. Waiting for your review.`;
    if (linkLive) return `Link sent ${day(form?.link?.sentAt)} to ${form?.link?.sentToName || 'buyer'} (${form?.link?.phoneHint})${form?.link?.openedAt ? `, opened ${day(form.link.openedAt)}` : ', not opened yet'}`;
    if (form?.link) return form.link.revoked ? 'The link was stopped.' : 'The link has expired.';
    if (termsReady === false) return 'Add the payment terms first. Then the form can be sent or filled here.';
    return 'Buyers fill their own details in English and check the Gujarati.';
  })();
  const chip = status === 'approved' ? ['good', 'Approved'] : status === 'submitted' ? ['warn', 'To review'] : linkLive ? ['acc', form?.link?.openedAt ? 'Opened' : 'Link sent'] : ['mute', 'Not sent'];

  return (
    <Card index={9} label="Buyer details form">
      <div className="ot-hd">
        <div><h3>Buyer details form</h3><p>{line}</p></div>
        <span className={`ot-chip c-${chip[0]}`}>{chip[1]}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {status === 'submitted' ? <button className="ot-btn sm dark" onClick={onReview}>Review details</button> : null}
        {status !== 'submitted' && status !== 'approved' && termsReady === false ? <button className="ot-btn sm dark" onClick={onAddTerms}>Add payment terms</button> : null}
        {status !== 'submitted' && termsReady ? <button className="ot-btn sm dark" onClick={() => setSendOpen(true)}>{linkLive ? 'Send again' : 'Send on WhatsApp'}</button> : null}
        {status !== 'submitted' && termsReady ? <button className="ot-btn sm" onClick={onFillHere}>Fill with buyer here</button> : null}
        {status === 'submitted' || status === 'approved' ? <button className="ot-btn sm" onClick={() => review('reopen')} disabled={!!busy}>{busy === 'reopen' ? 'Reopening…' : 'Reopen for changes'}</button> : null}
        {linkLive && status === 'draft' ? <button className="ou-linkbtn danger" onClick={() => review('revoke')} disabled={!!busy}>{busy === 'revoke' ? 'Stopping…' : 'Stop link'}</button> : null}
      </div>
      {msg ? <p className="faint" role="status" style={{ margin: '8px 0 0', fontSize: 12.5 }}>{msg}</p> : null}

      <SendDialog open={sendOpen} onClose={() => setSendOpen(false)} place={place} defaultMobile={defaultMobile} />

    </Card>
  );
}

/** Review a sent form. Approving saves the buyers and asks the page to create the booking form. */
export function BuyerFormReviewDialog({ open, onClose, form, place, termMonths, signed, onApproved }: {
  open: boolean; onClose: () => void; form: FormDoc | null;
  place: { projectId: string; unitId: string; bookingId: string };
  termMonths: number | null; signed: boolean;
  onApproved: (buyerCount: number) => void;
}) {
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [inPerson, setInPerson] = useState(false);
  const [term, setTerm] = useState('');
  const review = async (decision: 'approve' | 'reopen') => {
    if (decision === 'approve' && !signed && !termMonths && !Number(term)) { setMsg('Enter the agreement term in months.'); return; }
    setBusy(decision);
    setMsg('');
    try {
      const res = await callReview({ ...place, decision, verifiedInPerson: inPerson, agreementTermMonths: term });
      onClose();
      if (decision === 'approve') onApproved(Number(res.data?.buyers) || 1);
    } catch (e) {
      setMsg(errText(e));
    } finally {
      setBusy('');
    }
  };
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: '18px' } }}>
        <div className="ot" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0, borderRadius: 0, background: 'var(--ot-surface)' }}>
          <div className="ot-hd" style={{ margin: 0 }}>
            <div><h3>Review buyer details</h3><p>{form?.buyers?.length || 0} buyer{(form?.buyers?.length || 0) === 1 ? '' : 's'} · sent {day(form?.submittedAt)} {form?.submittedVia === 'office' ? 'at the office' : 'by the buyer'}</p></div>
          </div>
          {(form?.buyers || []).map((b, i) => {
            const files = form?.docs?.[b.key] || {};
            return (
              <div key={b.key} style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: i ? 14 : 0, borderTop: i ? '1px solid var(--ot-line)' : 0 }}>
                <b style={{ fontSize: 15 }}>Buyer {i + 1}: {fullName(b)}{i > 0 && b.relationToFirst ? <span className="faint" style={{ fontWeight: 400 }}> · {b.relationToFirst} of buyer 1</span> : null}</b>
                {panNameDiffers(b) ? <span className="ot-chip c-warn" style={{ width: 'fit-content' }}>Name differs from PAN</span> : null}
                <PaperBlock b={b} index={i} />
                <div className="ot-kvs" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '4px 16px', fontSize: 13 }}>
                  <span><span className="muted">{RELATION[b.fatherRelation] || 'Father / husband'}:</span> {b.fatherName} <span className="gu">({b.gu.fatherName})</span></span>
                  <span><span className="muted">Name on PAN:</span> {b.panName || '-'}</span>
                  <span><span className="muted">Born:</span> {b.dateOfBirth} · {b.gender}</span>
                  <span><span className="muted">Occupation:</span> {b.occupation === 'Other' ? b.occupationOther : OCCUPATIONS.find((o) => o.value === b.occupation)?.label || b.occupation}</span>
                  <span><span className="muted">Living in:</span> {b.residentStatus === 'nri' ? 'Outside India (NRI)' : 'India'}</span>
                  <span><span className="muted">Aadhaar:</span> {b.aadhaar ? `•••• •••• ${b.aadhaar.slice(-4)}` : '-'}</span>
                  <span><span className="muted">WhatsApp:</span> {b.whatsappSame ? 'same as mobile' : b.whatsapp || '-'}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {DOCS.map((d) => (files[d.key]
                    ? <a key={d.key} className="ot-btn sm" href={files[d.key]!.url} target="_blank" rel="noopener noreferrer">{d.label}</a>
                    : <span key={d.key} className="ot-chip c-mute">{d.label}: not uploaded</span>))}
                </div>
              </div>
            );
          })}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, paddingTop: 12, borderTop: '1px solid var(--ot-line)' }}>
            <span><span className="muted">Society committee:</span> {form?.societyCommittee === 'yes' ? 'Yes' : form?.societyCommittee === 'no' ? 'No' : 'Not answered'}</span>
            <span><span className="muted">Terms agreed:</span> {form?.termsAcceptedAt ? day(form.termsAcceptedAt) : 'No'}</span>
            {mobileGroups(form?.buyers || []).map((g) => (
              <span key={g.mobile}><span className="muted">{g.mobile}:</span> {form?.otp?.[g.mobile]?.at ? `Verified by OTP ${day(form.otp[g.mobile].at)}` : form?.checkedWithBuyer ? 'Checked in person at the office' : 'Not verified'} <span className="faint">· {g.names.join(', ')}</span></span>
            ))}
            {mobileGroups(form?.buyers || []).some((g) => !form?.otp?.[g.mobile]?.at) && !form?.checkedWithBuyer ? (
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}><input type="checkbox" checked={inPerson} onChange={(e) => setInPerson(e.target.checked)} />I verified the unverified buyers in person</label>
            ) : null}
          </div>
          <p className="faint" style={{ margin: 0, fontSize: 12.5 }}>Approving saves these buyers on the booking (updating existing ones), with PAN and Aadhaar numbers and the uploaded copies in KYC. Buyers already on the booking but not in this list are left as they are.</p>
          {!termMonths && !signed ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, maxWidth: 260 }} className="muted">
              Agreement term (months), for the booking form
              <input inputMode="numeric" value={term} onChange={(e) => setTerm(e.target.value.replace(/\D/g, '').slice(0, 3))} style={{ font: 'inherit', fontSize: 15, padding: '10px 12px', borderRadius: 12, border: '1px solid var(--ot-line)', background: 'var(--ot-surface)', color: 'var(--ot-ink)' }} />
            </label>
          ) : null}
          {msg ? <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--ot-bad-ink)' }}>{msg}</p> : null}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button className="ot-btn" onClick={onClose} disabled={!!busy}>Close</button>
            <button className="ot-btn" onClick={() => review('reopen')} disabled={!!busy}>{busy === 'reopen' ? 'Reopening…' : 'Send back for changes'}</button>
            <button className="ot-btn dark" onClick={() => review('approve')} disabled={!!busy}>{busy === 'approve' ? 'Saving…' : signed ? 'Approve and save' : 'Approve and create booking form'}</button>
          </div>
        </div>
      </Dialog>
  );
}

function SendDialog({ open, onClose, place, defaultMobile }: {
  open: boolean; onClose: () => void; place: { projectId: string; unitId: string; bookingId: string };
  defaultMobile: string;
}) {
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ link: string; manualUrl: string; sent: boolean; sendError: string | null; name: string } | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!open) return;
    setPhone(String(defaultMobile || '').replace(/\D/g, '').slice(-10));
    setError('');
    setResult(null);
    setCopied(false);
  }, [defaultMobile, open]);
  const create = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await callInvite({ ...place, phone });
      setResult(res.data);
      // Only when the company number could not send it: staff send it from their own WhatsApp.
      if (!res.data.sent) window.open(res.data.manualUrl, '_blank', 'noopener');
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };
  const copy = () => {
    if (!result) return;
    navigator.clipboard?.writeText(result.link).then(() => setCopied(true)).catch(() => setCopied(false));
  };
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '18px' } }}>
      <div className="ot" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0, borderRadius: 0, background: 'var(--ot-surface)' }}>
        <div className="ot-hd" style={{ margin: 0 }}><div><h3>Send buyer details form</h3><p>Sends the private link on WhatsApp. Any older link stops working.</p></div></div>
        {result ? (
          <>
            {result.sent ? (
              <p role="status" style={{ margin: 0, fontSize: 13.5 }}>Sent on WhatsApp to +91 {phone}{result.name !== 'there' ? ` (${result.name})` : ''}.</p>
            ) : (
              <>
                <p role="status" style={{ margin: 0, fontSize: 13.5 }}>{result.sendError || 'The company number could not send it.'} Send it from your own WhatsApp instead:</p>
                <a className="ot-btn dark" href={result.manualUrl} target="_blank" rel="noopener noreferrer">Open WhatsApp</a>
              </>
            )}
            <button className="ot-btn" onClick={copy}>{copied ? 'Link copied' : 'Copy link'}</button>
            <p className="faint" style={{ margin: 0, fontSize: 12 }}>The link works for 7 days, until the buyer sends the form.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="ot-btn" onClick={onClose}>Done</button></div>
          </>
        ) : (
          <>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5 }} className="muted">
              WhatsApp number (buyer or a family member)
              <input autoFocus inputMode="tel" maxLength={10} placeholder="10 digits" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(-10))} style={{ font: 'inherit', fontSize: 15, padding: '10px 12px', borderRadius: 12, border: '1px solid var(--ot-line)', background: 'var(--ot-surface)', color: 'var(--ot-ink)', fontVariantNumeric: 'tabular-nums' }} />
            </label>
            {error ? <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--ot-bad-ink)' }}>{error}</p> : null}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="ot-btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="ot-btn dark" onClick={create} disabled={busy || phone.length !== 10}>{busy ? 'Sending…' : 'Send'}</button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
